import React, {
  useEffect,
  useState,
  useCallback,
  useRef,
  useMemo,
} from "react";
import { Box } from "@mui/material";
import InternalHeader from "../InternalPages/InternalHeader";
import { useSelector } from "react-redux";
import LibraryFilter from "./LibraryFilter";
import { TabsNavigation, Tab } from "pepsico-ds";
import ViewSummary from "../ViewSummary/ViewSummary";
import LibraryTabView from "./LibraryTabView";
import useDebounce from "../../hooks/useDebounce";
import PropTypes from "prop-types";
import { isEqual } from "lodash";

export default function LibraryPageLayout({ pageData, header }) {
  const selectedView = useSelector((state) => state.view);
  const isDarkTheme = useSelector((state) => state.theme);
  const { viewSummaryDrawer } = useSelector((store) => store.viewSummary);
  const [grid, setGrid] = useState(selectedView);
  const [pageHeader] = useState(header);
  const [searchText, setSearchText] = useState("");
  const [tab, setTab] = useState(0);
  const [loading, setLoading] = useState(false);

  const [cropType, setCropType] = useState("");
  const [countryFilter, setCountryFilter] = useState("");
  const [languageFilter, setLanguageFilter] = useState("");
  const [localSector, setLocalSector] = useState(null);

  const debouncedSearchText = useDebounce(searchText, 800);
  const [filteredData, setFilteredData] = useState({ subjectItems: [] });
  const isInitialLoadRef = useRef(true);
  const prevFilteredRef = useRef(filteredData);

  useEffect(() => setGrid(selectedView), [selectedView]);

  const searchItems = useCallback(
    (text, selectedCrop, selectedCountry, selectedLanguage, selectedLocal) => {
      const normalize = (value) => value?.toLowerCase() ?? "";

      const matchesEntry = (entry) => {
        const matchesText = text
          ? normalize(entry?.title).includes(normalize(text))
          : true;

        const matchesCrop = selectedCrop?.label
          ? normalize(entry?.cropType) === normalize(selectedCrop.label)
          : true;

        const matchesCountry = selectedCountry?.label
          ? (entry?.countries || []).some(
              (c) => normalize(c?.name) === normalize(selectedCountry.label)
            )
          : true;

        const matchesLanguage = selectedLanguage?.label
          ? (entry?.languages || []).some(
              (l) => normalize(l?.name) === normalize(selectedLanguage.label)
            )
          : true;

        const matchesSector = selectedLocal?.label
          ? (entry?.sectors || []).some(
              (s) => normalize(s?.name) === normalize(selectedLocal.label)
            )
          : true;

        return (
          matchesText &&
          matchesCrop &&
          matchesCountry &&
          matchesLanguage &&
          matchesSector
        );
      };

      const filterEntries = (entries = []) => entries.filter(matchesEntry);

      const filteredSubjects = pageData?.subjectItems
        ?.map((item) => {
          const subjectNameMatch = text
            ? normalize(item.subjectName).includes(normalize(text))
            : false;

          const documents = filterEntries(item.documents);
          const videos = filterEntries(item.videos);
          const links = filterEntries(item.links);

          const hasAnyMatch =
            subjectNameMatch ||
            documents.length ||
            videos.length ||
            links.length;

          if (!hasAnyMatch) return null;

          return {
            subjectId: item.subjectId,
            subjectName: item.subjectName,
            documents,
            videos,
            links,
          };
        })
        .filter(Boolean);

      return { subjectItems: filteredSubjects };
    },
    [pageData] // only depends on pageData
  );

  const memoizedFilteredData = useMemo(() => {
    return searchItems(
      debouncedSearchText,
      cropType,
      countryFilter,
      languageFilter,
      localSector
    );
  }, [
    debouncedSearchText,
    cropType,
    countryFilter,
    languageFilter,
    localSector,
    searchItems,
  ]);

  useEffect(() => {
    let isMounted = true;
    setLoading(true);

    const timeout = setTimeout(
      () => {
        if (!isMounted) return;

        if (!isEqual(prevFilteredRef.current, memoizedFilteredData)) {
          setFilteredData(memoizedFilteredData);
          prevFilteredRef.current = memoizedFilteredData;
        }

        setLoading(false);

        if (isInitialLoadRef.current) {
          isInitialLoadRef.current = false;
        }
      },
      isInitialLoadRef.current ? 2000 : 800
    );

    return () => {
      isMounted = false;
      clearTimeout(timeout);
    };
  }, [memoizedFilteredData]);
  const handleClick = (value) => {
    setLoading(true);
    setTab(value);
    setTimeout(() => setLoading(false), 800);
  };

  const setSearchTextData = (text) => {
    setSearchText(text);
  };

  return (
    <Box
      sx={{
        mt: "16px",
        display: "flex",
        flexDirection: "column",
        gap: "24px",
        paddingX: { xs: "16px", md: "53px" },
      }}
    >
      <InternalHeader header={pageHeader} data-testid="internal-header" />
      <Box sx={{ zIndex: 1000 }}>
        <LibraryFilter
          setGrid={setGrid}
          pageData={pageData}
          header={header}
          grid={grid}
          setSearchTextData={setSearchTextData}
          setCropType={setCropType}
          setCountryFilter={setCountryFilter}
          setLanguageFilter={setLanguageFilter}
          setLocalSector={setLocalSector}
          localSector={localSector}
          data-testid="library-filter"
        />
      </Box>

      <TabsNavigation selectedIndex={tab} isDark={isDarkTheme}>
        <Tab label="All" onClick={() => handleClick(0)} />
        <Tab label="Documents" onClick={() => handleClick(1)} />
        <Tab label="Videos" onClick={() => handleClick(2)} />
        <Tab label="Links" onClick={() => handleClick(3)} />
      </TabsNavigation>

      <>
        {tab === 0 && (
          <LibraryTabView
            pageData={filteredData?.subjectItems}
            loading={loading}
            data-testid="library-tab-view"
          />
        )}
        {tab === 1 && (
          <LibraryTabView
            loading={loading}
            pageData={filteredData?.subjectItems?.map(
              ({ subjectId, subjectName, documents }) => ({
                subjectId,
                subjectName,
                documents,
              })
            )}
            data-testid="library-tab-view"
          />
        )}
        {tab === 2 && (
          <LibraryTabView
            loading={loading}
            pageData={filteredData?.subjectItems?.map(
              ({ subjectId, subjectName, videos }) => ({
                subjectId,
                subjectName,
                videos,
              })
            )}
            data-testid="library-tab-view"
          />
        )}
        {tab === 3 && (
          <LibraryTabView
            loading={loading}
            pageData={filteredData?.subjectItems?.map(
              ({ subjectId, subjectName, links }) => ({
                subjectId,
                subjectName,
                links,
              })
            )}
            data-testid="library-tab-view"
          />
        )}
      </>
      {viewSummaryDrawer && <ViewSummary data-testid="view-summary" />}
    </Box>
  );
}
LibraryPageLayout.propTypes = {
  pageData: PropTypes.shape({
    subjectItems: PropTypes.arrayOf(
      PropTypes.shape({
        subjectId: PropTypes.string.isRequired,
        subjectName: PropTypes.string,
        documents: PropTypes.array,
        videos: PropTypes.array,
        links: PropTypes.array,
      })
    ),
  }).isRequired,
  header: PropTypes.string,
};
